<script setup lang="ts">
import Multiselect from '@vueform/multiselect'
import { useField } from 'vee-validate'
import { mergeRules } from 'core/services/form'

const originalProps = defineProps<{
  name: string
  label?: string
  placeholder?: string
  options?: any
  multi?: boolean
  requiredError?: string
  pluralize?: (count: number) => string
  noResultsText?: string
  noOptionsText?: string
  disableStoryblok?: boolean
  modelValue?: any
  tooltip?: any
  rules?: any
  disabled?: boolean
  permanentField?: any
  hideBorder?: boolean
  groups?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'changed', 'select'])
const ga4 = useAnalytics()

const props = originalProps.disableStoryblok ? originalProps : useStoryblokProps(originalProps)
const multiselect = ref()

const mergedRules = computed(() => originalProps.disabled ? undefined : mergeRules(originalProps.rules, props.requiredError))
const { value, errorMessage, setState } = useField(props.name, mergedRules, { validateOnValueUpdate: false })

function pluralizeDefault(values: any[]) {
  if (values.length === 1)
    return 'Vybraná 1 položka'
  if (values.length < 5)
    return `Vybrané ${values.length} položky`
  return `Vybraných ${values.length} položek`
}
const pluralizeInternal = props.pluralize || pluralizeDefault
watch(value, () => {
  emit('changed', value.value, props.name)
  if (value.value) setState({ errors: [] })
})
watch(errorMessage, () => {
  if (errorMessage.value) ga4.errorEvent({ event_label: originalProps?.name })
})
function addPermanentField() {
  if (originalProps.permanentField) {
    if (!multiselect.value?.filteredOptions.some((option: any) => option.code === originalProps.permanentField?.code))
      multiselect.value?.filteredOptions.push(originalProps.permanentField)
  }
}
function deselect(option: any) {
  if (props.multi) return
  multiselect.value.close()
  multiselect.value.select(option)
}
function select(option: any) {
  ga4.trackEvent({ event_action: 'dropdown', event_label: originalProps?.name })
  emit('select', option)
}
</script>

<template>
  <div class="relative w-full">
    <BaseLabel v-if="props.label" :tooltip="props.tooltip">
      {{ props.label }}
    </BaseLabel>
    <Multiselect
      ref="multiselect"
      v-model="value"
      :groups="groups"
      :data-test-id="`FieldDropdown|${name}`"
      :hide-selected="false"
      value-prop="code"
      :can-clear="false"
      :close-on-select="props.multi ? false : true"
      :mode="props.multi ? 'multiple' : 'single'"
      :multiple-label="pluralizeInternal"
      :options="options || props.options"
      group-options="value"
      :disabled="disabled"
      :placeholder="props.placeholder"
      :no-results-text="props.noResultsText"
      :no-options-text="props.noOptionsText"
      caret
      :classes="{
        container: `multiselectContainer relative mx-auto w-full h-11.5 flex items-center justify-end box-border cursor-pointer rounded-xl leading-snug bg-white outline-none ${errorMessage ? 'border border-error shadow-form' : `${!hideBorder && 'focus:border-primary shadow-form border border-form hover:border-primary'}`}`,
        wrapper: 'w-full h-full',
        containerDisabled: `cursor-default !bg-gray-light ${errorMessage || 'focus:border-gray hover:border-gray'}`,
        placeholder: 'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 text-gray-400 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5',
        singleLabel: 'flex items-center h-full max-w-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 pr-8 box-border rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5',
        singleLabelText: 'overflow-ellipsis overflow-hidden block whitespace-nowrap max-w-full',
        multipleLabel: 'flex items-center h-full absolute left-0 top-0 pointer-events-none bg-transparent leading-snug pl-3.5 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3.5',
        caret: `flex ${disabled ? 'bg-multiselect-caret-disabled' : 'bg-multiselect-caret'}
          bg-center bg-no-repeat w-2.5 h-full box-content absolute right-3 z-10 flex-shrink-0 flex-grow-0 transition-transform transform pointer-events-none rtl:mr-0 rtl:ml-3.5`,
        caretOpen: 'rotate-180 pointer-events-auto',
        dropdown: 'custom-scrollbar py-2.5 pl-2.5 max-h-60 absolute -left-px -right-px -bottom-1 transform translate-y-full border border-primary rounded-xl -mt-px overflow-y-scroll z-50 bg-white flex flex-col',
        dropdownHidden: 'hidden',
        options: 'flex flex-col list-none',
        option: 'flex items-center justify-start box-border text-left cursor-pointer text-base leading-snug p-2.5 rounded-xl',
        optionPointed: 'text-hover bg-primary/15',
        optionSelected: 'font-600 selected-check',
        optionSelectedPointed: 'text-hover bg-primary/15 font-600 selected-check',
        clear: 'pr-3.5 relative z-10 opacity-40 transition duration-300 flex-shrink-0 flex-grow-0 flex hover:opacity-80 rtl:pr-0 rtl:pl-3.5',
        noOptions: `py-2 px-3 text-gray-600 bg-white text-left rtl:text-right ${permanentField && 'hidden'}`,
        noResults: !permanentField && 'py-2 px-3 text-gray-600 bg-white text-left rtl:text-right',
        fakeInput: 'multiselect-fake-input',
        groupLabel: 'font-bold my-2',
      }"
      @deselect="deselect"
      @open="addPermanentField"
      @search-change="addPermanentField"
      @select="select"
    >
      <template #beforelist>
        <slot name="beforelist" />
      </template>
      <template #option="{ option }">
        <div v-if="props.multi">
          <AppIcon icon="far-check" size="16" class="mr-2.5 invisible" />
          {{ option.label }}
        </div>
        <slot name="option" :option="option" />
      </template>
      <template #singlelabel="{ value: val }">
        <slot name="singlelabel" :value="val" />
      </template>
    </Multiselect>
    <div v-if="isMobile && !disabled && !props.multi" class="relative">
      <select
        v-model="value" :data-test-id="`FieldDropdown|${name}`" class="absolute top-0 w-full h-full opacity-0"
      >
        <option v-for="option in options" :value="option.code || option">
          {{ option.label || option }}
        </option>
        <option v-if="permanentField" :value="permanentField.code">
          {{ permanentField.label }}
        </option>
      </select>
    </div>
    <FormFieldErrorMessage v-if="errorMessage">
      {{ errorMessage }}
    </FormFieldErrorMessage>
  </div>
</template>

<style scoped>
.selected-check div {
  visibility: visible !important;
}
</style>
